import { useNavigate } from 'react-router-dom';
import { Sparkles, Clock, Droplets, Bed, StretchHorizontal, ShowerHead, Smile, ArrowRight, Check, Target, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';

function DailyEssentialsPage() {
  const navigate = useNavigate();

  const essentials = [
    {
      number: 1,
      title: "Adjust Your Alarm",
      icon: Clock,
      color: "from-amber-400 to-orange-500",
      description: "Set your alarm 20 minutes earlier than usual. This small change can have a big impact on both personal and professional aspects of your life. Waking up slightly earlier allows you to start your day with a calm, intentional mindset. Use this extra time for activities like meditation, exercise, or enjoying a peaceful breakfast to boost mental clarity and overall well-being."
    },
    {
      number: 2,
      title: "Start with Iced Lemon Water",
      icon: Droplets,
      color: "from-yellow-400 to-lime-500",
      description: "Kickstart your day by drinking a glass of iced lemon water. This refreshing ritual offers numerous health benefits. The cool temperature invigorates your senses and jumpstarts your metabolism, while the lemon provides a vital dose of vitamin C, crucial for a healthy immune system. Additionally, lemon water aids digestion, balances pH levels, and promotes hydration after a night's rest."
    },
    {
      number: 3,
      title: "Make Your Bed",
      icon: Bed,
      color: "from-pink-400 to-rose-500",
      description: "Spend two minutes each morning making your bed. This simple task can set a positive tone for the day. It fosters a sense of order and accomplishment from the outset, contributing to greater productivity and efficiency. A neatly made bed also offers a welcoming environment for relaxation at the end of the day."
    },
    {
      number: 4,
      title: "Stretch for Ten Minutes",
      icon: StretchHorizontal,
      color: "from-emerald-400 to-teal-500",
      description: "Incorporate ten minutes of stretching into your morning routine. Stretching enhances flexibility, increases blood flow, and improves posture. It reduces muscle stiffness and tension accumulated during sleep, preparing your body for daily activities. This practice also boosts mental clarity and alleviates stress, promoting a mindful and energetic approach to the day."
    },
    {
      number: 5,
      title: "Daily Shower",
      icon: ShowerHead,
      color: "from-blue-400 to-cyan-500",
      description: "Shower daily, choosing whether to do so in the morning or evening. Regular showers are vital for personal hygiene and overall health, removing dirt, sweat, and bacteria from the skin. They refresh both body and mind, offering a chance for relaxation and self-care."
    },
    {
      number: 6,
      title: "Nightly Oral Hygiene",
      icon: Smile,
      color: "from-violet-400 to-purple-500",
      description: "Every night, floss and brush your teeth before bed. This routine helps remove food particles and plaque, preventing tooth decay and gum disease. Good oral hygiene not only ensures fresh breath but also contributes to overall health, reducing the risk of systemic conditions such as heart disease and diabetes."
    },
    {
      number: 7,
      title: "Complete Your Daily Challenge",
      icon: Target,
      color: "from-rose-400 to-red-500",
      description: "Each day brings a new challenge designed to push you outside your comfort zone and foster growth. These challenges are carefully curated to build confidence, resilience, and new skills. Completing your daily challenge creates momentum and reinforces your commitment to personal transformation."
    },
    {
      number: 8,
      title: "Nightly Journaling",
      icon: BookOpen,
      color: "from-indigo-400 to-blue-500",
      description: "End each day with reflection through journaling. Take a few minutes to write about your experiences, gratitude, and insights from the day. This practice promotes self-awareness, emotional processing, and mental clarity. Regular journaling helps track your progress and creates a meaningful record of your transformation journey."
    }
  ];

  const handleContinue = () => {
    // Mark that user has seen the essentials page
    localStorage.setItem('seenEssentials', 'true');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary-50 via-white to-accent-50">
      {/* Header */}
      <header className="pt-12 pb-8 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-primary font-semibold">Your Foundation for Success</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            8 Daily Essentials
          </h1>
          <p className="text-xl text-primary font-medium mb-6">
            Begin Your Transformation Journey
          </p>
          <div className="text-secondary-700 leading-relaxed space-y-4 text-left md:text-center">
            <p>
              Today marks the beginning of your transformative journey. This program is designed to seamlessly integrate daily habits into your life, fostering a holistic lifestyle.
            </p>
            <p>
              Each day, you'll engage in activities that benefit your mental, emotional, and physical well-being. By the end of this program, you'll have a comprehensive toolkit to enhance your life.
            </p>
            <p className="font-medium text-secondary-900">
              Some commitments are essential. Here are eight daily practices you'll pledge to uphold:
            </p>
          </div>
        </div>
      </header>

      {/* Essentials Grid */}
      <main className="container mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {essentials.map((essential) => {
            const IconComponent = essential.icon;
            return (
              <div
                key={essential.number}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                data-testid={`essential-${essential.number}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${essential.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Essential #{essential.number}
                    </span>
                    <h3 className="text-xl font-serif font-bold text-secondary-900 mt-1">
                      {essential.title}
                    </h3>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm leading-relaxed">
                  {essential.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Commitment Section */}
        <div className="max-w-3xl mx-auto mt-12">
          <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 rounded-3xl p-8 text-center border-2 border-primary/20">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
              Your Commitment to Wellness
            </h2>
            <p className="text-secondary-700 mb-8 leading-relaxed">
              By committing to these practices, you will cultivate a foundation for well-being that supports both your personal and professional growth. These eight essentials will become the cornerstone of your daily routine throughout your Radiant 40 journey.
            </p>
            <Button
              onClick={handleContinue}
              className="bg-primary hover:bg-primary/90 text-white px-10 py-6 rounded-full shadow-lg text-lg font-semibold hover:scale-105 transition-all duration-300"
              data-testid="begin-journey-button"
            >
              I'm Ready to Begin
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </main>

      {/* Decorative Footer */}
      <footer className="py-8 text-center">
        <p className="text-secondary-500 text-sm">
          Your transformation starts with small, consistent steps ✨
        </p>
      </footer>
    </div>
  );
}

export default DailyEssentialsPage;
